import { ApprovalMode } from "../approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "../ClientTools";
import { createClientTool, toolError, toolSuccess } from "../ClientTools";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface SeekInput {
  /** Timestamp in nanoseconds since Unix epoch. */
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

function execute(
  input: SeekInput,
  deps?: ClientToolDeps,
): ClientToolExecutionResult {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }

  if (input.timestamp === undefined) {
    return toolError("timestamp is required");
  }

  if (!deps.isOnVisualizerPage?.()) {
    return toolError(
      "User is not on the visualizer page. Use visualize_open_panel or visualize_open_file first.",
    );
  }

  const published = deps.publishWorkspaceAction?.({
    type: "seek_timer",
    payload: { timestamp: BigInt(input.timestamp) },
  });
  if (!published) {
    return toolError("Failed to publish workspace action");
  }

  return toolSuccess("seek", { timestamp: input.timestamp });
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<SeekInput>({
  name: "visualize_seek",
  description: `Seek the visualizer timeline to a specific timestamp. Only works if the user is already on the visualizer page; returns an error otherwise. Use visualize_open_panel or visualize_open_file first to open the visualizer.`,
  input_schema: {
    type: "object",
    properties: {
      timestamp: {
        type: "integer",
        description:
          "Timestamp in nanoseconds since Unix epoch. The timeline will jump to this moment.",
      },
    },
    required: ["timestamp"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Seek",
  displayDescription: "Seeks the visualizer timeline to a timestamp.",
});
